import { OfferOption } from '@/types/area-response.types'
import { Module, Store } from 'vuex'

export const NAMESPACE = 'orderFlow'

export enum Property {
  Offers = 'offers',
  SelectedOffer = 'selectedOffer',
  SelectedOfferId = 'selectedOfferId',
  SelectedAlternativeDelivery = 'selectedAlternativeDelivery',
  SelectedOperatorChangeDate = 'selectedOperatorChangeDate',
  ResetFlow = 'resetFlow',
  UpdateOffers = 'updateOffers',
}

export type OrderFlowState = {
  [Property.Offers]: OfferOption[] | [];
  [Property.SelectedOffer]: OfferOption | null;
  [Property.SelectedOfferId]: number | null;
  [Property.SelectedAlternativeDelivery]: boolean | null;
  [Property.SelectedOperatorChangeDate]: string | null;
}

const namespaced = (getter: Property): string => `${NAMESPACE}/${getter}`

type OrderFlowStore = Store<OrderFlowState>
type GenericStore = Store<any> // eslint-disable-line @typescript-eslint/no-explicit-any

export const getOffers = (store: GenericStore): OfferOption[] | [] => store.getters[namespaced(Property.Offers)]
export const getSelectedOffer = (store: GenericStore): OfferOption | null => store.getters[namespaced(Property.SelectedOffer)]
export const getSelectedOfferId = (store: GenericStore): number | null => store.getters[namespaced(Property.SelectedOfferId)]
export const setSelectedOfferId = (store: GenericStore, id: number | null): void => store.commit(namespaced(Property.SelectedOfferId), id)
export const getSelectedAlternativeDelivery = (store: GenericStore): boolean | null => store.getters[namespaced(Property.SelectedAlternativeDelivery)]
export const setSelectedAlternativeDelivery = (
  store: GenericStore,
  selectedAlternativeDelivery: boolean
): void => store.commit(namespaced(Property.SelectedAlternativeDelivery), selectedAlternativeDelivery)
export const getSelectedOperatorChangeDate = (store: GenericStore): string | null => store.getters[namespaced(Property.SelectedOperatorChangeDate)]
export const setSelectedOperatorChangeDate = (
  store: GenericStore,
  selectedOperatorChangeDate: string | null
): void => store.commit(namespaced(Property.SelectedOperatorChangeDate), selectedOperatorChangeDate)

export const resetFlowStore = (store: GenericStore): Promise<void> => store.dispatch(namespaced(Property.ResetFlow))
export const updateOffers = (store: GenericStore): Promise<void> => store.dispatch(namespaced(Property.UpdateOffers))

const OrderFlowStore: Module<OrderFlowState, {}> = {
  namespaced: true,
  state: {
    [Property.Offers]: [],
    [Property.SelectedOffer]: null,
    [Property.SelectedOfferId]: null,
    [Property.SelectedAlternativeDelivery]: null,
    [Property.SelectedOperatorChangeDate]: null
  },
  getters: {
    [Property.Offers]: /* istanbul ignore next */ state => state[Property.Offers],
    [Property.SelectedOfferId]: /* istanbul ignore next */ state => state[Property.SelectedOfferId],
    [Property.SelectedOffer]: state => state[Property.Offers].find(offer => offer.Id === state[Property.SelectedOfferId]),
    [Property.SelectedAlternativeDelivery]: /* istanbul ignore next */ state => state[Property.SelectedAlternativeDelivery],
    [Property.SelectedOperatorChangeDate]: /* istanbul ignore next */ state => state[Property.SelectedOperatorChangeDate]
  },
  mutations: {
    [Property.Offers]: (state, offers: OfferOption[] | []) => {
      const indexedOffers = [...offers].map((opt, _i) => ({ ...opt, _i })) // index insertion helps with deeply equal object selection (ANSL-230)
      state[Property.Offers] = indexedOffers
    },
    [Property.SelectedOfferId]: /* istanbul ignore next */ (state, id: number | null) => { state[Property.SelectedOfferId] = id },
    [Property.SelectedOffer]: /* istanbul ignore next */ (state, offer: OfferOption | null) => { state[Property.SelectedOffer] = offer },
    [Property.SelectedAlternativeDelivery]: /* istanbul ignore next */ (state, alt: boolean | null) => { state[Property.SelectedAlternativeDelivery] = alt },
    [Property.SelectedOperatorChangeDate]: /* istanbul ignore next */ (state, date: string | null) => { state[Property.SelectedOperatorChangeDate] = date }
  },
  actions: {
    [Property.ResetFlow]: ({ commit }) => {
      commit(Property.SelectedOfferId, null)
      commit(Property.SelectedAlternativeDelivery, null)
    },
    [Property.UpdateOffers]: ({ commit, rootGetters }) => { commit(Property.Offers, rootGetters['area/areaInfo'].OfferOptions) }
  }
}

export default OrderFlowStore
